/**
 * useRouteTracker — writes a `psyforge:last_visit:<route>` timestamp to
 * localStorage on every route change. Used by `useFeatureLastVisited` to
 * surface "Last used Nd ago" badges on the Home feature explorer.
 *
 * Mounted once inside `DashboardLayout`.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const PREFIX = 'psyforge:last_visit:';
const TRACKED_PREFIXES = [
  '/personality',
  '/insights',
  '/mentor',
  '/quizzes',
  '/liker',
  '/career',
  '/relationships',
  '/cards',
  '/science',
  '/reflect',
  '/integral',
  '/tps-assessment-hub',
  '/history',
  '/iq-test',
  '/reports',
  '/bookmarks',
  '/astrology',
];

function rootRoute(pathname: string): string | null {
  for (const p of TRACKED_PREFIXES) {
    if (pathname === p || pathname.startsWith(p + '/')) return p;
  }
  return null;
}

export function useRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = rootRoute(location.pathname);
    if (!root) return;
    try {
      window.localStorage.setItem(PREFIX + root, String(Date.now()));
    } catch {
      // localStorage may be disabled — best effort
    }
  }, [location.pathname]);
}

export function readLastVisit(route: string): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PREFIX + route);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}